import Task from './Task';
import { createError } from '../error';

const getClaimsForStaffingUserPath = '/staffing_user/{{staffing_users_id}}/claims';
const savePrimaryClaimForStaffingUserPath = '/staffing_user/{{staffing_users_id}}/positions/{{positions_id}}/primary_claim';
const savePrimaryClaimForOtherStaffingUserPath = '/staffing_user/{{staffing_users_id}}/positions/{{positions_id}}/other_users_primary_claim';
const saveSecondaryClaimForStaffingUserPath = '/staffing_user/{{staffing_users_id}}/events/{{events_id}}/secondary_claims';
const uploadSecondaryClaimReceipt = '/staffing_user/{{staffing_users_id}}/events/{{events_id}}/secondary_claims/{{claims_id}}/upload';
const withdrawSecondaryClaim = '/staffing_user/{{staffing_users_id}}/events/{{events_id}}/secondary_claims/{{claims_id}}/withdraw';
const getOtherUserClaimsForStaffingUserPath = '/staffing_user/{{staffing_users_id}}/events/{{events_id}}/other_users_claims';

class ClaimSaveTask extends Task {
  constructor(
    $q,
    APIService,
    LogService,
    DataSyncService,
    ClaimService,
    DocumentService
  ) {
    super();
    this.$q = $q;
    this.APIService = APIService;
    this.LogService = LogService;
    this.DataSyncService = DataSyncService;
    this.ClaimService = ClaimService;
    this.DocumentService = DocumentService;

    this._serializedPrimaryClaim = null;
    this._serializedSecondaryClaims = [];
    this._serializedOtherUsersPrimaryClaims = [];
    this._primaryClaim = null;
    this._secondaryClaims = [];
    this._otherUsersPrimaryClaims = [];
  }

  /**
   * Public properties
   */

  get canExecute() {
    return this.APIService.isOnline;
  }

  set primaryClaim(claim) {
    this._primaryClaim = claim;
  }

  get primaryClaim() {
    return this._primaryClaim;
  }

  get secondaryClaims() {
    return this._secondaryClaims;
  }

  get otherUsersPrimaryClaims() {
    return this._otherUsersPrimaryClaims;
  }

  set serializedPrimaryClaim(data) {
    this._serializedPrimaryClaim = data;
  }

  get serializedPrimaryClaim() {
    return this._serializedPrimaryClaim;
  }

  set serializedSecondaryClaims(claims) {
    this._serializedSecondaryClaims = claims;
  }

  get serializedSecondaryClaims() {
    return this._serializedSecondaryClaims;
  }

  set serializedOtherUsersPrimaryClaims(claims) {
    this._serializedOtherUsersPrimaryClaims = claims;
  }

  get serializedOtherUsersPrimaryClaims() {
    return this._serializedOtherUsersPrimaryClaims;
  }

  get updatingObjectTypesAndIds() {
    let updating = [];
    if (this.serializedPrimaryClaim) {
      updating.push(['Claim', this.serializedPrimaryClaim.primary_claim.id]);
      updating.push(['Position', this.serializedPrimaryClaim.position.id]);
    }
    this.serializedSecondaryClaims.forEach(serializedSecondaryClaim => {
      updating.push(['Claim', serializedSecondaryClaim.id]);
    });
    this.serializedOtherUsersPrimaryClaims.forEach(serializedPrimaryClaim => {
      updating.push(['Claim', serializedPrimaryClaim.id]);
    });
    return updating;
  }

  get loggedInStaffUserId() {
    return this.DataSyncService && this.DataSyncService.staffUser && this.DataSyncService.staffUser.id;
  }

  /**
   * Public methods
   */

  populate(data) {
    this.staffUserId = data.staffUserId;
    this.serializedPrimaryClaim = data.serializedPrimaryClaim;
    this.serializedSecondaryClaims = data.serializedSecondaryClaims;
  }

  serialize() {
    return {
      staffUserId: this.staffUserId,
      serializedPrimaryClaim: this.serializedPrimaryClaim,
      serializedSecondaryClaims: this.serializedSecondaryClaims,
    };
  }

  execute() {
    let allSavedClaims = [];
    return this._savePrimaryClaimIfPresent()
      .then(savedPrimaryClaim => {
        if (savedPrimaryClaim) {
          allSavedClaims.push(savedPrimaryClaim);
        }
        return this._saveSecondaryClaims();
      })
      .then(savedSecondaryClaims => {
        if (savedSecondaryClaims) {
          allSavedClaims = allSavedClaims.concat(savedSecondaryClaims);
        }
        return this._saveOtherUsersPrimaryClaims();
      })
      .then(savedOtherUsersPrimaryClaims => {
        if (savedOtherUsersPrimaryClaims) {
          allSavedClaims = allSavedClaims.concat(savedOtherUsersPrimaryClaims);
        }
        return allSavedClaims;
      })
      .catch(err => {
        this.LogService.error("Error saving claims:", err);
        return this.$q.reject(err);
      });
  }

  addPositionForPrimaryClaim(position) {
    let claim = position.primaryClaim;
    this.serializedPrimaryClaim = this.ClaimService.serializePrimaryClaim(claim, position);
    this._primaryClaim = claim;
  }

  addSecondaryClaim(claim) {
    const serializedClaim = this.ClaimService.serialize(claim);
    if (claim.receiptDocument && claim.receiptDocument.hasFileForUpload && !claim.receiptDocument.isUploaded) {
      // Serialize document and add to secondary claim
      serializedClaim.serializedDocument = this.DocumentService.serialize(claim.receiptDocument);
    } else {
      serializedClaim.serializedDocument = null;
    }

    this.serializedSecondaryClaims.push(serializedClaim);
  }

  addPrimaryClaimForOtherUsersPosition(staffUser, position, primaryClaim) {
    let serializedClaim = this.ClaimService.serializePrimaryClaim(primaryClaim, position);
    this.serializedOtherUsersPrimaryClaims.push(serializedClaim);
  }

  _savePrimaryClaimIfPresent() {
    if (this.serializedPrimaryClaim) {
      let data = this.serializedPrimaryClaim;
      let url = savePrimaryClaimForStaffingUserPath
        .replace('{{staffing_users_id}}', this.loggedInStaffUserId)
        .replace('{{positions_id}}', data.position.id);
      return this.APIService.post(url, data)
        .catch(response => {
          this.LogService.error("Error posting to url", url, "response:", response);
          return this.$q.reject(createError({
            error: 'ErrorSavingClaim',
            message: 'There was an error saving the claim',
            response,
          }));
        })
        .then(response => {
          // TODO: Determine whether we should save position?
          // Instead for now we just update actual_start and actual_end
          // Previously we just saved actual_start and actual_end
          // ... but this doesn't make sense if we're in the background

          // this.updatePositionFromAPI(position, response.data.position);

          let primaryClaim = this.ClaimService.create(response.data.primary_claim);
          this.primaryClaim = primaryClaim;
          this.DataSyncService.setObjectById('Claim', primaryClaim.id, primaryClaim);
          return primaryClaim;
        });
    } else {
      return this.$q.when(null);
    }
  }

  _saveSecondaryClaims() {
    const promises = this.serializedSecondaryClaims
      .map(serializedClaim => {
        const path = saveSecondaryClaimForStaffingUserPath
          .replace('{{staffing_users_id}}', this.loggedInStaffUserId)
          .replace('{{events_id}}', serializedClaim.events_id);

        // Pull out document for upload, if it exists
        let serializedDocument = serializedClaim.serializedDocument;
        delete serializedClaim.serializedDocument;

        return this.APIService.post(path, serializedClaim)
          .catch(response => {
            return this.$q.reject(createError({
              error: 'ErrorSavingClaim',
              message: 'There was an error saving the claim',
              response,
            }))
          })
          .then(response => {
            return this.ClaimService.create(response.data);
          })
          .then(claim => {
            this.secondaryClaims.push(claim);
            if (serializedDocument) {
              // Upload document
              let receiptPath = uploadSecondaryClaimReceipt
                .replace('{{staffing_users_id}}', serializedClaim.staffing_users_id)
                .replace('{{events_id}}', claim.events_id)
                .replace('{{claims_id}}', claim.id);
              let receiptUrl = this.APIService.convertPathToURL(receiptPath);
              return this.DocumentService.create(serializedDocument)
                .then(document => this.DocumentService.upload(document, receiptUrl, null, 'receipt'))
                .then(() => {
                  return claim;
                })
                .catch(err => {
                  this.LogService.error("Error uploading document for claim", claim, document);
                  return this.$q.reject(createError({
                    error: 'ErrorUploadingClaimReceipt',
                    message: 'There was an error uploading the receipt for a claim',
                    wrapped: err,
                  }));
                });
            } else {
              return claim;
            }
          })
          .then(claim => {
            this.DataSyncService.setObjectById('Claim', claim.id, claim);
            return claim;
          });
      });
    return this.$q.all(promises);
  }

  _saveOtherUsersPrimaryClaims() {
    const promises = this.serializedOtherUsersPrimaryClaims
      .map(serializedPrimaryClaim => {
        let position = serializedPrimaryClaim.position;
        let path = savePrimaryClaimForOtherStaffingUserPath
          .replace('{{staffing_users_id}}', this.loggedInStaffUserId)
          .replace('{{positions_id}}', position.id);
        return this.APIService.post(path, serializedPrimaryClaim)
          .catch(response => {
            this.LogService.error("Error saving other user's primary claim, POST to path", path, "response:", response);
            return this.$q.reject(createError({
              error: 'ErrorSavingClaim',
              message: 'There was an error saving the claim',
              response,
            }));
          })
          .then(response => {
            const claimData = response.data.primary_claim;
            return this.ClaimService.create(claimData);
          })
          .then(claim => {
            this.DataSyncService.setObjectById('Claim', claim.id, claim);
            this.otherUsersPrimaryClaims.push(claim);
            return claim;
          });
      });
    return this.$q.all(promises);
  }
}

export default ClaimSaveTask;
